/**
 * LocationSchema.jsx (iter159) — Custom location-code structure per warehouse.
 *
 * Use cases:
 *   • Small/flat warehouse: drop Zone + Bin → codes like "A01-B01-L01"
 *   • Pallet-only DC: keep just Aisle + Bay → "A01-B01"
 *   • Mixed network: per-warehouse override with company default fallback
 *
 * Settings cascade: per-warehouse override > company default > built-in default.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Path, Trash, CheckCircle, Info, GridFour } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;
const SEGMENT_KEYS = ['zone', 'aisle', 'bay', 'level', 'bin'];
const SEP_OPTIONS = [
  { key: '-', label: '- (hyphen)' },
  { key: '/', label: '/ (slash)' },
  { key: '.', label: '. (dot)' },
  { key: ' ', label: 'space' },
  { key: '__none__', label: 'none (concatenated)' },
];
const SEP_NONE = '__none__';
const sepFromUi = (k) => (k === SEP_NONE ? '' : k);
const sepToUi = (s) => (s === '' ? SEP_NONE : s);

const SAMPLE_VALUES = { zone: 'A', aisle: '01', bay: '02', level: '03', bin: '04' };

function previewCode(segments, separator) {
  return segments.filter((s) => s.enabled)
    .map((s) => SAMPLE_VALUES[s.key])
    .join(separator);
}

export default function LocationSchemaPage() {
  const [warehouses, setWarehouses] = useState([]);
  const [scope, setScope] = useState('default'); // 'default' or a warehouse id
  const [segments, setSegments] = useState([]);
  const [separator, setSeparator] = useState('-');
  // UI value uses '__none__' as a sentinel because shadcn Select can't take ''
  const sepUi = sepToUi(separator);
  const [allSchemas, setAllSchemas] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load warehouses + schemas
  useEffect(() => {
    (async () => {
      try {
        const [whRes, schemasRes] = await Promise.all([
          axios.get(`${API}/api/warehouses`, { withCredentials: true }),
          axios.get(`${API}/api/location-schemas`, { withCredentials: true }),
        ]);
        setWarehouses(whRes.data?.warehouses || whRes.data || []);
        setAllSchemas(schemasRes.data?.items || []);
      } catch (e) {
        toast.error('Failed to load warehouses or schemas');
      } finally { setLoading(false); }
    })();
  }, []);

  // Load the effective schema for the current scope
  useEffect(() => {
    (async () => {
      try {
        const params = scope === 'default' ? {} : { warehouse_id: scope };
        const r = await axios.get(`${API}/api/location-schemas/effective`, { params, withCredentials: true });
        setSegments(r.data.segments || []);
        setSeparator(r.data.separator || '-');
      } catch (e) { /* keep current state */ }
    })();
  }, [scope]);

  const toggleSeg = (key) => {
    setSegments((segs) => segs.map((s) => s.key === key ? { ...s, enabled: !s.enabled } : s));
  };
  const renameSeg = (key, label) => {
    setSegments((segs) => segs.map((s) => s.key === key ? { ...s, label } : s));
  };
  const moveSeg = (key, dir) => {
    setSegments((segs) => {
      const i = segs.findIndex((s) => s.key === key);
      if (i < 0) return segs;
      const j = dir === 'up' ? i - 1 : i + 1;
      if (j < 0 || j >= segs.length) return segs;
      const copy = segs.slice();
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        warehouse_id: scope === 'default' ? null : scope,
        separator,
        segments: segments.map((s) => ({ key: s.key, enabled: !!s.enabled, label: s.label || s.key })),
      };
      const r = await axios.put(`${API}/api/location-schemas`, body, { withCredentials: true });
      toast.success('Schema saved');
      // Refresh the list of saved schemas (for the "saved" indicator)
      const lr = await axios.get(`${API}/api/location-schemas`, { withCredentials: true });
      setAllSchemas(lr.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const removeOverride = async () => {
    const saved = allSchemas.find((s) => (s.warehouse_id || null) === (scope === 'default' ? null : scope));
    if (!saved?.id) { toast.error('Nothing saved to remove'); return; }
    if (saved.warehouse_id === null) { toast.error('Cannot delete the company default — edit it instead.'); return; }
    try {
      await axios.delete(`${API}/api/location-schemas/${saved.id}`, { withCredentials: true });
      toast.success('Per-warehouse override removed — falls back to company default.');
      const lr = await axios.get(`${API}/api/location-schemas`, { withCredentials: true });
      setAllSchemas(lr.data?.items || []);
      setScope('default');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const sample = useMemo(() => previewCode(segments, separator), [segments, separator]);
  const enabledCount = segments.filter((s) => s.enabled).length;
  const scopeIsSaved = allSchemas.some((s) =>
    (s.warehouse_id || null) === (scope === 'default' ? null : scope) && !s._is_default,
  );

  return (
    <Layout>
      <div className="p-6 max-w-4xl mx-auto" data-testid="location-schema-page">
        <div className="flex items-center gap-3 mb-1">
          <Path size={28} weight="duotone" className="text-brand" />
          <h1 className="text-2xl font-bold tracking-tight">Location Code Schema</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          Choose which segments make up a location code, what they&apos;re labelled, and how they&apos;re joined.
          Per-warehouse settings override the company default; existing locations are unaffected.
        </p>

        {/* Scope selector */}
        <div className="bg-card border border-border p-4 mb-4">
          <Label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Edit schema for</Label>
          <div className="flex items-center gap-2 mt-2">
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="max-w-md" data-testid="schema-scope-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="default" data-testid="schema-scope-default">
                  Company default (applies to all warehouses without an override)
                </SelectItem>
                {warehouses.map((w) => (
                  <SelectItem key={w.id} value={w.id} data-testid={`schema-scope-wh-${w.id}`}>
                    {w.name} — {w.code || w.slug}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {scopeIsSaved && scope !== 'default' && (
              <Button variant="outline" size="sm" onClick={removeOverride} data-testid="schema-remove-override">
                <Trash size={12} className="mr-1" /> Remove override
              </Button>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground mt-2 flex items-center gap-1">
            <Info size={12} />
            {scope === 'default'
              ? 'This applies to every warehouse without its own override.'
              : scopeIsSaved
                ? 'This warehouse has its own schema (overrides the company default).'
                : 'This warehouse currently falls back to the company default — saving will create an override.'}
          </div>
        </div>

        {/* Segments */}
        <div className="bg-card border border-border p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <Label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Segments</Label>
            <Badge variant="outline" data-testid="schema-enabled-count">{enabledCount} of 5 enabled</Badge>
          </div>
          {loading ? (
            <div className="text-sm text-muted-foreground py-4">Loading…</div>
          ) : (
            <div className="space-y-2">
              {segments.map((seg, idx) => (
                <div
                  key={seg.key}
                  className={`flex items-center gap-3 border ${seg.enabled ? 'border-brand/40 bg-brand/5' : 'border-border bg-muted/30 opacity-60'} px-3 py-2`}
                  data-testid={`schema-seg-${seg.key}`}
                >
                  <div className="flex flex-col">
                    <button type="button" onClick={() => moveSeg(seg.key, 'up')} disabled={idx === 0} className="text-xs px-1 disabled:opacity-30" data-testid={`schema-seg-${seg.key}-up`}>▲</button>
                    <button type="button" onClick={() => moveSeg(seg.key, 'down')} disabled={idx === segments.length - 1} className="text-xs px-1 disabled:opacity-30" data-testid={`schema-seg-${seg.key}-down`}>▼</button>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={seg.enabled}
                      onChange={() => toggleSeg(seg.key)}
                      data-testid={`schema-seg-${seg.key}-toggle`}
                    />
                    <span className="text-xs uppercase tracking-widest font-bold w-16">{seg.key}</span>
                  </label>
                  <div className="flex-1">
                    <Input
                      value={seg.label}
                      onChange={(e) => renameSeg(seg.key, e.target.value)}
                      placeholder={seg.key}
                      maxLength={24}
                      disabled={!seg.enabled}
                      data-testid={`schema-seg-${seg.key}-label`}
                      className="font-mono text-sm"
                    />
                  </div>
                  <div className="text-[10px] text-muted-foreground w-20 text-right font-mono">
                    sample: <span className="font-bold">{SAMPLE_VALUES[seg.key]}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Separator */}
        <div className="bg-card border border-border p-4 mb-4">
          <Label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Separator</Label>
          <Select value={sepUi} onValueChange={(v) => setSeparator(sepFromUi(v))}>
            <SelectTrigger className="max-w-md mt-2" data-testid="schema-separator-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SEP_OPTIONS.map((o) => (
                <SelectItem key={o.key} value={o.key} data-testid={`schema-sep-${o.key === SEP_NONE ? 'none' : o.key}`}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Preview */}
        <div className="border-2 border-dashed border-brand/40 bg-brand/5 p-5 mb-4 text-center">
          <div className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground mb-2 flex items-center justify-center gap-1">
            <GridFour size={14} weight="bold" /> Sample location code
          </div>
          <code className="font-mono text-3xl font-bold tracking-wide" data-testid="schema-preview">
            {sample || <span className="text-muted-foreground">(no segments enabled)</span>}
          </code>
          <div className="text-[11px] text-muted-foreground mt-2">
            from values: {SEGMENT_KEYS.map((k) => `${k}=${SAMPLE_VALUES[k]}`).join(', ')}
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={save} disabled={busy || enabledCount === 0} className="bg-brand hover:bg-brand/90 text-white font-bold" data-testid="schema-save">
            {busy ? 'Saving…' : <><CheckCircle size={14} className="mr-1" weight="fill" /> Save schema</>}
          </Button>
        </div>
      </div>
    </Layout>
  );
}
